import React, { useState, useEffect, useCallback } from "react";
import { useFetchAllJobTasks } from "@labeling-tools/api/lebron/react-query-hooks";
import { JobTaskStatus, JobTaskType } from "@augmodo/lebron-browser-client";
import { Table, Text, Pagination, Badge } from "@mantine/core";
import { Link, useNavigate } from "react-router-dom";
import { getCurrentUser } from "@/utils/auth";
import { TASK_TYPE_ROUTE } from "@/config/data";

const TASK_TYPE_LABEL: Record<string, string> = {
  [JobTaskType.OUT_OF_STOCK_CONFIRMATION]: "Confirmation",
  OUT_OF_STOCK_CONFIRMATION_BENCHMARK: "Benchmark",
  [JobTaskType.OUT_OF_STOCK_CONFIRMATION_QA]: "Confirmation QA",
  [JobTaskType.OUT_OF_STOCK_SELECTION]: "Labeling",
  [JobTaskType.OUT_OF_STOCK_BOX_DRAW]: "Annotation",
  [JobTaskType.COVERAGE_REVIEW]: "Coverage",
  [JobTaskType.COVERAGE_REVIEW_QA]: "Coverage QA",
};

export const MyTasksTabContent: React.FC = () => {
  const navigate = useNavigate();
  const [fetchTasksParams, setFetchTasksParams] = useState<any>(null);
  const { data, isLoading } = useFetchAllJobTasks(fetchTasksParams);

  useEffect(() => {
    const init = async () => {
      const user = await getCurrentUser();
      if (user?.uid) {
        setFetchTasksParams({ assignee: "me", status: JobTaskStatus.PENDING });
      }
    };
    init();
  }, []);

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>, task: any) => {
      const routeSuffix = TASK_TYPE_ROUTE[task.type] ?? "confirmation";
      const to = `/jobs/${task.job_id}/${routeSuffix}`;
      if (e.metaKey || e.ctrlKey) {
        window.open(to, "_blank");
      } else {
        navigate(to);
      }
    },
    [navigate],
  );

  if (!data && isLoading) {
    return <div>Loading...</div>;
  }

  const tasks = data?.tasks ?? [];

  if (tasks.length === 0 && !isLoading && fetchTasksParams) {
    return <Text>No tasks assigned to you.</Text>;
  }

  const rows = tasks.map((task) => {
    const routeSuffix = TASK_TYPE_ROUTE[task.type] ?? "confirmation";
    const to = `/jobs/${task.job_id}/${routeSuffix}`;
    return (
      <Table.Tr
        key={task.id}
        onClick={(e) => handleRowClick(e, task)}
        style={{ cursor: "pointer" }}
        className="hover:bg-gray-50 transition-colors"
      >
        <Table.Td>
          <Link
            to={to}
            style={{ textDecoration: "underline" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Text fw={600} size="sm">
              {task.id}
            </Text>
          </Link>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{task.job_id}</Text>
        </Table.Td>
        <Table.Td>
          <Badge variant="light" size="sm">
            {TASK_TYPE_LABEL[task.type] ?? task.type}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{new Date(task.created_at).toLocaleString()}</Text>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <div>
      <Table withTableBorder stickyHeader>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Task ID</Table.Th>
            <Table.Th>Job ID</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Created Date</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
      {(data?.total_pages ?? 0) > 1 && (
        <div className="flex justify-center mt-6">
          <Pagination
            total={data?.total_pages ?? 1}
            value={data?.page ?? 1}
            onChange={(value) =>
              setFetchTasksParams({ ...fetchTasksParams, page: value })
            }
            size="sm"
          />
        </div>
      )}
    </div>
  );
};
